/*
 * Copyright (c) Arbeidskracht Groep B.V. - all rights reserved
 */

import {combineEpics, Epic} from 'redux-observable';
import {filter, ignoreElements, map, mapTo, mergeMap, tap} from 'rxjs/operators';
import {Route as LoginUIRoute} from '../routes';
import {isActionOf} from 'typesafe-actions';
import {
	confirmEmployeeRegistrationSceneActivated,
	confirmEmployeeRegistrationSceneBackButtonClicked,
	confirmEmployeeRegistrationSceneDeactivated,
	confirmEmployeeRegistrationSceneEnterPasswordValidationException,
	confirmEmployeeRegistrationSceneEnterPhoneVerificationCodeUnexpectedError,
	confirmEmployeeRegistrationSceneEnterPhoneVerificationCodeValidationException,
	confirmEmployeeRegistrationScenePasswordInputChanged,
	confirmEmployeeRegistrationScenePasswordStrengthEvaluated,
	confirmEmployeeRegistrationScenePasswordSubmitted,
	confirmEmployeeRegistrationScenePhoneVerificationCodeSubmitted,
	confirmEmployeeRegistrationSceneRequestRestartPhoneVerification,
	confirmEmployeeRegistrationSceneRequestRetryPhoneVerification,
	confirmEmployeeRegistrationSceneRestartPhoneVerificationUnexpectedError,
	confirmEmployeeRegistrationSceneRetryPhoneVerificationUnexpectedError,
	confirmEmployeeRegistrationSceneStateChanged,
	confirmEmployeeRegistrationSceneStateRetrieved
} from '../scenes/ConfirmEmployeeRegistration/actions';
import {employeeRegistrationResource, iamStompClient} from '../services';
import {
	iamApiEmployeeRegistrationEnterPasswordValidationException,
	iamApiEmployeeRegistrationEnterPhoneVerificationCodeUnexpectedError,
	iamApiEmployeeRegistrationPasswordStrengthEvaluated,
	iamApiEmployeeRegistrationRestartPhoneVerificationUnexpectedError,
	iamApiEmployeeRegistrationRetryPhoneVerificationUnexpectedError,
	iamApiEmployeeRegistrationStateRetrieved,
	iamApiFlexServiceWorkerRegistrationEnterPhoneVerificationCodeValidationException
} from '../services/IamApi/actions';
import {stompClientSubscribedToTopic, stompClientUnsubscribedFromTopic, stompMessageReceived} from '../services/Stomp/actions';
import {EmployeeRegistrationStateDTO} from '../generated/iam-client';
import {STATE_CHANGE_FOR_EMPLOYEE_REGISTRATION} from '../services/Stomp/EventType';
import {EMPLOYEE_REGISTRATION_STATE_TOPIC_PREFIX} from '../services/Stomp/topics';
import {violationsWithErrorMessages} from '../model/validation';
import {navigate} from '../components/OutHook';

const employeeRegistrationIdFromQueryString: () => string =
	() => new URLSearchParams(window.document.location.search).get('employeeRegistrationId') || '';

const subscribeToEmployeeRegistrationStateTopicOnSceneActivate: Epic = (action$) => action$.pipe(
	filter(isActionOf(confirmEmployeeRegistrationSceneActivated)),
	mergeMap(() => iamStompClient.subscribe(EMPLOYEE_REGISTRATION_STATE_TOPIC_PREFIX + employeeRegistrationIdFromQueryString()))
);

// noinspection DuplicatedCode
const unsubscribeFromEmployeeRegistrationStateTopicOnSceneDeactivate: Epic = (action$) => {
	let subscribedTopic: string | null = null;

	action$.pipe(
		filter(isActionOf(stompClientSubscribedToTopic)),
		filter((action) => action.payload.topic.startsWith(EMPLOYEE_REGISTRATION_STATE_TOPIC_PREFIX))
	).subscribe((action) => subscribedTopic = action.payload.topic);

	action$.pipe(
		filter(isActionOf(stompClientUnsubscribedFromTopic)),
		filter((action) => action.payload.topic === subscribedTopic)
	).subscribe(() => subscribedTopic = null);

	return action$.pipe(
		filter(isActionOf(confirmEmployeeRegistrationSceneDeactivated)),
		filter(() => subscribedTopic !== null),
		mergeMap(() => iamStompClient.unsubscribe(String(subscribedTopic)))
	);
};

const retrieveRegistrationStateOnSceneActivate: Epic = (action$) => action$.pipe(
	filter(isActionOf(confirmEmployeeRegistrationSceneActivated)),
	mergeMap(() => employeeRegistrationResource.employeeRegistrationState(employeeRegistrationIdFromQueryString()))
);

const notifySceneOfRetrievedState: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiEmployeeRegistrationStateRetrieved)),
	map((action) => confirmEmployeeRegistrationSceneStateRetrieved(action.payload))
);

const notifySceneOfStateChange: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter((action) => action.payload.eventType === STATE_CHANGE_FOR_EMPLOYEE_REGISTRATION),
	map((action) => confirmEmployeeRegistrationSceneStateChanged(
		{state: action.payload.eventBody as unknown as EmployeeRegistrationStateDTO}
	))
);

const retryPhoneVerificationOnRequest: Epic = (action$) => action$.pipe(
	filter(isActionOf(confirmEmployeeRegistrationSceneRequestRetryPhoneVerification)),
	mergeMap(() => employeeRegistrationResource.retryPhoneVerification(employeeRegistrationIdFromQueryString()))
);

const notifySceneOfPhoneVerificationRetryUnexpectedError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiEmployeeRegistrationRetryPhoneVerificationUnexpectedError)),
	mapTo(confirmEmployeeRegistrationSceneRetryPhoneVerificationUnexpectedError())
);

const restartPhoneVerificationOnRequest: Epic = (action$) => action$.pipe(
	filter(isActionOf(confirmEmployeeRegistrationSceneRequestRestartPhoneVerification)),
	mergeMap(() => employeeRegistrationResource.restartPhoneVerification(employeeRegistrationIdFromQueryString()))
);

const notifySceneOfPhoneVerificationRestartUnexpectedError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiEmployeeRegistrationRestartPhoneVerificationUnexpectedError)),
	mapTo(confirmEmployeeRegistrationSceneRestartPhoneVerificationUnexpectedError())
);

const enterPhoneVerificationCodeOnSubmit: Epic = (action$) => action$.pipe(
	filter(isActionOf(confirmEmployeeRegistrationScenePhoneVerificationCodeSubmitted)),
	mergeMap((action) => employeeRegistrationResource.enterPhoneVerificationCode(
		employeeRegistrationIdFromQueryString(), action.payload.command
	))
);

const notifySceneOfPhoneVerificationCodeValidationError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiFlexServiceWorkerRegistrationEnterPhoneVerificationCodeValidationException)),
	map((action) => {
		const violations = violationsWithErrorMessages(action.payload.violations);

		return confirmEmployeeRegistrationSceneEnterPhoneVerificationCodeValidationException({
			violations: {
				verificationCode: violations.verificationCode || []
			}
		});
	})
);

const notifySceneOfPhoneVerificationCodeUnexpectedError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiEmployeeRegistrationEnterPhoneVerificationCodeUnexpectedError)),
	mapTo(confirmEmployeeRegistrationSceneEnterPhoneVerificationCodeUnexpectedError())
);

const evaluatePasswordStrengthOnChange: Epic = (action$) => action$.pipe(
	filter(isActionOf(confirmEmployeeRegistrationScenePasswordInputChanged)),
	mergeMap((action) => employeeRegistrationResource.evaluatedPasswordStrength(
		employeeRegistrationIdFromQueryString(), action.payload.evaluationRequest
	))
);

const notifySceneOfEvaluatedPasswordStrength: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiEmployeeRegistrationPasswordStrengthEvaluated)),
	map((action) => confirmEmployeeRegistrationScenePasswordStrengthEvaluated(action.payload))
);

const enterPasswordOnSubmit: Epic = (action$) => action$.pipe(
	filter(isActionOf(confirmEmployeeRegistrationScenePasswordSubmitted)),
	mergeMap((action) => employeeRegistrationResource.enterPassword(employeeRegistrationIdFromQueryString(), action.payload.command))
);

const notifySceneOfEnterPasswordValidationError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiEmployeeRegistrationEnterPasswordValidationException)),
	map((action) => {
		const violations = violationsWithErrorMessages(action.payload.violations);

		return confirmEmployeeRegistrationSceneEnterPasswordValidationException({
			violations: {
				password: violations.password || []
			}
		});
	})
);

const navigateToLoginOnClick: Epic = (action$) => action$.pipe(
	filter(isActionOf(confirmEmployeeRegistrationSceneBackButtonClicked)),
	tap(() => navigate(LoginUIRoute.LOGIN)),
	ignoreElements()
);

const confirmEmployeeRegistrationEpics: Epic = combineEpics(
	subscribeToEmployeeRegistrationStateTopicOnSceneActivate,
	unsubscribeFromEmployeeRegistrationStateTopicOnSceneDeactivate,
	retrieveRegistrationStateOnSceneActivate,
	notifySceneOfRetrievedState,
	notifySceneOfStateChange,
	retryPhoneVerificationOnRequest,
	notifySceneOfPhoneVerificationRetryUnexpectedError,
	restartPhoneVerificationOnRequest,
	notifySceneOfPhoneVerificationRestartUnexpectedError,
	enterPhoneVerificationCodeOnSubmit,
	notifySceneOfPhoneVerificationCodeValidationError,
	notifySceneOfPhoneVerificationCodeUnexpectedError,
	evaluatePasswordStrengthOnChange,
	notifySceneOfEvaluatedPasswordStrength,
	enterPasswordOnSubmit,
	notifySceneOfEnterPasswordValidationError,
	navigateToLoginOnClick
);

export default confirmEmployeeRegistrationEpics;
